import { UrlTreeNode } from './selection';
import { SCENARIO_ID } from '@/context';

// Backend error_message for a zero-URL map (dead/blocked domain); must match NO_PAGES_FOUND_REASON in backend site_map.py.
export const SITE_MAP_NO_PAGES_REASON = 'no_pages_found';

// Shared empty-map predicate (dock + modal agree): failed+no_pages_found, or a legacy ready+0 row.
export const isSiteMapEmpty = (
  status: string,
  totalUrls: number,
  errorMessage: string | null | undefined,
): boolean =>
  (status === 'failed' && errorMessage === SITE_MAP_NO_PAGES_REASON) ||
  (status === 'ready' && totalUrls === 0);

// Set when a background site map is kicked off; the dock polls until ready, then opens the modal.
export const SITEMAP_PENDING_KEY = `__qc_sitemap_pending_${SCENARIO_ID}`;

// Set on upgrade so the dock reopens the modal after Stripe's redirect: 'all' or legacy '1' (the saved selection).
export const SITEMAP_RESUME_KEY = `__qc_sitemap_resume_${SCENARIO_ID}`;

// Set once a ready map's selection has been scraped, so the dock stops offering it.
export const SITEMAP_CONSUMED_KEY = `__qc_sitemap_consumed_${SCENARIO_ID}`;

// JSON {key, until}: "Not now" on one ready map, from the dock's X or the review's Cancel/Esc/overlay.
export const SITEMAP_SNOOZE_KEY = `__qc_sitemap_snooze_${SCENARIO_ID}`;

// The scan the dock already auto-opened, so a reload or agent switch leaves it in the dock.
export const SITEMAP_AUTO_OPENED_KEY = `__qc_sitemap_auto_opened_${SCENARIO_ID}`;

// The page load an upgrade was started from, so the dock doesn't reopen the review on the way to Billing.
export const SITEMAP_UPGRADE_STARTED_IN_KEY = `__qc_sitemap_upgrade_started_in_${SCENARIO_ID}`;

const PAGE_LOAD_ID = `${Date.now()}:${Math.random()}`;

export const SITEMAP_SNOOZE_MS = 24 * 60 * 60_000;

export type SiteMapSnoozeState = 'none' | 'active' | 'expired';

export type SiteMapResumeTarget = 'selection' | 'all';

export const siteMapKey = (tree: Pick<UrlTreeNode, 'path'>, totalUrls: number): string =>
  `${tree.path}:${totalUrls}`;

// Timestamp (ms) holding the modal shut until the onboarding discount + confetti land.
// Session-scoped so a fresh tab doesn't inherit a stale deferral.
export const SITEMAP_CELEBRATE_UNTIL_KEY = `__qc_sitemap_celebrate_until_${SCENARIO_ID}`;

export const SITEMAP_CELEBRATE_MS = 2600;

// How long after kickoff the dock keeps polling an idle (404) map: covers the
// onboarding race where the flag lands before the backend creates the row.
const PENDING_FRESH_MS = 10 * 60_000;

export const clearSiteMapSnooze = () => localStorage.removeItem(SITEMAP_SNOOZE_KEY);

export const snoozeSiteMap = (mapKey: string) =>
  localStorage.setItem(
    SITEMAP_SNOOZE_KEY,
    JSON.stringify({ key: mapKey, until: Date.now() + SITEMAP_SNOOZE_MS }),
  );

// When this map's snooze ends, or null if the map isn't snoozed.
export const siteMapSnoozeUntil = (mapKey: string): number | null => {
  let snooze: unknown;
  try {
    snooze = JSON.parse(localStorage.getItem(SITEMAP_SNOOZE_KEY) ?? 'null');
  } catch {
    return null;
  }
  if (typeof snooze !== 'object' || snooze === null) return null;
  const { key, until } = snooze as { key?: unknown; until?: unknown };
  if (key !== mapKey || typeof until !== 'number' || !Number.isFinite(until)) return null;
  return until;
};

export const siteMapSnoozeState = (mapKey: string): SiteMapSnoozeState => {
  const until = siteMapSnoozeUntil(mapKey);
  if (until === null) return 'none';
  return Date.now() < until ? 'active' : 'expired';
};

// One scan: the map's shape plus its kickoff time, so rescanning the same site is a new scan.
export const siteMapScanKey = (mapKey: string): string =>
  `${mapKey}@${localStorage.getItem(SITEMAP_PENDING_KEY) ?? ''}`;

export const markSiteMapAutoOpened = (scanKey: string) =>
  localStorage.setItem(SITEMAP_AUTO_OPENED_KEY, scanKey);

export const wasSiteMapAutoOpened = (scanKey: string): boolean =>
  localStorage.getItem(SITEMAP_AUTO_OPENED_KEY) === scanKey;

// Re-arms one auto-open for a later page load (the Stripe return or the next visit), never for this one.
export const markSiteMapUpgradeStarted = () => {
  localStorage.removeItem(SITEMAP_AUTO_OPENED_KEY);
  sessionStorage.setItem(SITEMAP_UPGRADE_STARTED_IN_KEY, PAGE_LOAD_ID);
};

export const siteMapUpgradeStartedThisLoad = (): boolean =>
  sessionStorage.getItem(SITEMAP_UPGRADE_STARTED_IN_KEY) === PAGE_LOAD_ID;

export const flagSiteMapResume = (target: SiteMapResumeTarget) =>
  localStorage.setItem(SITEMAP_RESUME_KEY, target === 'all' ? 'all' : '1');

export const siteMapResumeTarget = (): SiteMapResumeTarget | null => {
  const raw = localStorage.getItem(SITEMAP_RESUME_KEY);
  if (raw == null) return null;
  return raw === 'all' ? 'all' : 'selection';
};

export const setSiteMapPending = () => {
  localStorage.setItem(SITEMAP_PENDING_KEY, String(Date.now()));
  localStorage.removeItem(SITEMAP_AUTO_OPENED_KEY);
  sessionStorage.removeItem(SITEMAP_UPGRADE_STARTED_IN_KEY);
  clearSiteMapSnooze();
};

export const isSiteMapPendingFresh = (): boolean => {
  const raw = localStorage.getItem(SITEMAP_PENDING_KEY);
  if (raw == null) return false;
  const startedAt = Number(raw);
  return Number.isFinite(startedAt) && Date.now() - startedAt < PENDING_FRESH_MS;
};

export const clearSiteMapFlags = () => {
  localStorage.removeItem(SITEMAP_PENDING_KEY);
  localStorage.removeItem(SITEMAP_RESUME_KEY);
  sessionStorage.removeItem(SITEMAP_UPGRADE_STARTED_IN_KEY);
  clearSiteMapSnooze();
};

// Wipe every site-map flag (incl. consumed and auto-opened markers + deferral) on outright discard.
export const forgetSiteMap = () => {
  clearSiteMapFlags();
  localStorage.removeItem(SITEMAP_CONSUMED_KEY);
  localStorage.removeItem(SITEMAP_AUTO_OPENED_KEY);
  sessionStorage.removeItem(SITEMAP_CELEBRATE_UNTIL_KEY);
};

// Hold the review modal shut for `SITEMAP_CELEBRATE_MS` from now.
export const deferSiteMapModal = () => {
  sessionStorage.setItem(SITEMAP_CELEBRATE_UNTIL_KEY, String(Date.now() + SITEMAP_CELEBRATE_MS));
};

// Remaining defer time in ms (0 once the celebration window has elapsed).
export const siteMapDeferRemainingMs = (): number => {
  const until = Number(sessionStorage.getItem(SITEMAP_CELEBRATE_UNTIL_KEY) ?? 0);
  return Math.max(0, until - Date.now());
};

export const clearSiteMapDefer = () => sessionStorage.removeItem(SITEMAP_CELEBRATE_UNTIL_KEY);
